// Споделена дизайн-система на Livupp — единен вид на всички екрани.
// Токени (цвят, разстояния, радиус, сянка) + типографска скала + примитиви.
// Рафинирането тук се отразява навсякъде, без да се пипа всеки екран.

import React, { useState } from 'react';
import { useWide } from './layout';
import { t } from './i18n';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TextInputProps,
  View,
} from 'react-native';

// Неутралите са с лек навигационен (син) уклон — избрани, не сиви по подразбиране.
// Брандът на Livupp: диагонален градиент тъмно синьо → магента (от логото)
export const BRAND = {
  navy: '#001461',
  indigo: '#2B187D',
  purple: '#7621A6',
  magenta: '#C22AD0',
};

export const COLORS = {
  primary: '#7621A6', // лилавото от средата на градиента
  primaryDark: '#4B198D',
  primarySoft: '#F5EBFB',
  bg: '#F6F5FB', // светъл фон с лек лилав оттенък
  card: '#FFFFFF',
  text: '#1A1450', // тъмното индиго от бранда
  textDim: '#3D3670',
  muted: '#6E6A8C', // сиво с лилав оттенък
  border: '#E9E6F4',
  hair: '#F0EEF8',
  danger: '#DC2626',
  dangerSoft: '#FDECEC',
  success: '#0E9F6E',
  successSoft: '#E6F6EF',
  warn: '#D97706',
  warnSoft: '#FDF1E3',
};

// Разстояния и радиуси — една стълба навсякъде
export const SP = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 };
export const RADIUS = { sm: 10, md: 14, lg: 18, pill: 999 };

// Мека, ненатрапчива сянка за картите
const SHADOW = Platform.select({
  ios: { shadowColor: '#0B1B3B', shadowOpacity: 0.06, shadowRadius: 12, shadowOffset: { width: 0, height: 4 } },
  android: { elevation: 2 },
  default: {},
}) as object;

// Центрира съдържанието на широк екран (уеб); на телефон е no-op.
// На широк уеб (със странична навигация) съдържанието диша до 960 —
// телефонната колона от 640 изглежда като „уголемено приложение".
export function Constrain({ children }: { children: React.ReactNode }) {
  const wide = useWide();
  return <View style={{ width: '100%', maxWidth: wide ? 960 : 640, alignSelf: 'center' }}>{children}</View>;
}

export function Screen({ children }: { children: React.ReactNode }) {
  return (
    <KeyboardAvoidingView
      style={{ flex: 1, backgroundColor: COLORS.bg }}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView
        style={{ flex: 1, backgroundColor: COLORS.bg }}
        contentContainerStyle={{ padding: SP.lg, paddingBottom: 120 }}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        showsVerticalScrollIndicator={false}
      >
        <Constrain>{children}</Constrain>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

export function Card({ children, style }: { children: React.ReactNode; style?: object }) {
  return <View style={[styles.card, style]}>{children}</View>;
}

export function Title({ children, style }: { children: React.ReactNode; style?: object }) {
  return <Text style={[styles.title, style]}>{children}</Text>;
}

// Малък раздел-етикет (главни букви, приглушен) — за заглавия на секции
export function SectionLabel({ children, style }: { children: React.ReactNode; style?: object }) {
  return <Text style={[styles.sectionLabel, style]}>{children}</Text>;
}

export function Label({ children }: { children: React.ReactNode }) {
  return <Text style={styles.label}>{children}</Text>;
}

export function Muted({ children, style }: { children: React.ReactNode; style?: object }) {
  return <Text style={[{ color: COLORS.muted, fontSize: 14, lineHeight: 20 }, style]}>{children}</Text>;
}

export function Divider() {
  return <View style={{ height: 1, backgroundColor: COLORS.hair, marginVertical: SP.sm }} />;
}

export function Input(props: TextInputProps) {
  return <TextInput placeholderTextColor={COLORS.muted} {...props} style={[styles.input, props.style]} />;
}

export function Button({
  title,
  onPress,
  variant = 'primary',
  disabled,
  loading,
  style,
}: {
  title: string;
  onPress: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  disabled?: boolean;
  loading?: boolean;
  style?: object;
}) {
  const bg =
    variant === 'primary' ? COLORS.primary : variant === 'danger' ? COLORS.danger : variant === 'ghost' ? 'transparent' : COLORS.card;
  const fg = variant === 'secondary' ? COLORS.primary : variant === 'ghost' ? COLORS.primary : '#fff';
  const filled = variant === 'primary' || variant === 'danger';
  // На широк уеб бутонът е по съдържанието си, не разтегнат до 960 —
  // цял ред син правоъгълник изглежда телефонно. Подаденият style печели.
  const wide = useWide();
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled || loading}
      style={({ pressed }) => [
        styles.button,
        wide && { alignSelf: 'flex-start', minWidth: 220, paddingHorizontal: 28 },
        { backgroundColor: variant === 'primary' && pressed ? COLORS.primaryDark : bg },
        filled ? SHADOW : null,
        variant === 'secondary' && { borderWidth: 1.5, borderColor: COLORS.primary },
        { opacity: disabled ? 0.45 : pressed ? 0.9 : 1 },
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator color={fg} />
      ) : (
        <Text
          numberOfLines={1}
          adjustsFontSizeToFit
          minimumFontScale={0.7}
          style={{ color: fg, fontWeight: '700', fontSize: 16, textAlign: 'center', letterSpacing: 0.1 }}
        >
          {title}
        </Text>
      )}
    </Pressable>
  );
}

// Падащо меню (единичен или множествен избор)
export function Select({
  placeholder,
  options,
  value,
  values,
  multi,
  onChange,
}: {
  placeholder: string;
  options: readonly string[];
  value?: string | null;
  values?: string[];
  multi?: boolean;
  onChange: (next: any) => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedLabel = multi ? (values?.length ? values.join(', ') : '') : (value ?? '');

  return (
    <>
      <Pressable
        onPress={() => setOpen(true)}
        style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
      >
        <Text style={{ color: selectedLabel ? COLORS.text : COLORS.muted, fontSize: 16, flex: 1 }} numberOfLines={1}>
          {selectedLabel || placeholder}
        </Text>
        <Text style={{ color: COLORS.muted, marginLeft: 8 }}>▾</Text>
      </Pressable>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable
          onPress={() => setOpen(false)}
          style={{ flex: 1, backgroundColor: 'rgba(11,27,59,0.45)', justifyContent: 'center', padding: 24 }}
        >
          <Pressable style={{ backgroundColor: COLORS.card, borderRadius: RADIUS.lg, maxHeight: '75%', padding: SP.sm }}>
            <ScrollView showsVerticalScrollIndicator={false}>
              {options.map((opt) => {
                const selected = multi ? values?.includes(opt) : value === opt;
                return (
                  <Pressable
                    key={opt}
                    onPress={() => {
                      if (multi) {
                        const current = values ?? [];
                        onChange(current.includes(opt) ? current.filter((v) => v !== opt) : [...current, opt]);
                      } else {
                        onChange(opt);
                        setOpen(false);
                      }
                    }}
                    style={{
                      paddingVertical: 14,
                      paddingHorizontal: 14,
                      borderRadius: RADIUS.sm,
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      backgroundColor: selected ? COLORS.primarySoft : 'transparent',
                    }}
                  >
                    <Text style={{ fontSize: 16, color: selected ? COLORS.primary : COLORS.text, fontWeight: selected ? '700' : '400' }}>
                      {opt}
                    </Text>
                    {selected ? <Text style={{ color: COLORS.primary, fontWeight: '700' }}>✓</Text> : null}
                  </Pressable>
                );
              })}
            </ScrollView>
            {multi && <Button title={t('Готово')} onPress={() => setOpen(false)} />}
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

// Хапче-етикет със статус (по подразбиране синьо; подавай семантичен цвят)
export function Badge({ text, color = COLORS.primary }: { text: string; color?: string }) {
  return (
    <View
      style={{
        backgroundColor: `${color}18`,
        borderRadius: RADIUS.pill,
        paddingHorizontal: 10,
        paddingVertical: 4,
        alignSelf: 'flex-start',
      }}
    >
      <Text style={{ color, fontSize: 12, fontWeight: '700', letterSpacing: 0.2 }}>{text}</Text>
    </View>
  );
}

// Малко хапче за филтри/тагове (по-леко от Badge)
export function Chip({ text, active, onPress }: { text: string; active?: boolean; onPress?: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={{
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: RADIUS.pill,
        backgroundColor: active ? COLORS.primary : COLORS.card,
        borderWidth: 1,
        borderColor: active ? COLORS.primary : COLORS.border,
      }}
    >
      <Text style={{ color: active ? '#fff' : COLORS.textDim, fontWeight: '600', fontSize: 14 }}>{text}</Text>
    </Pressable>
  );
}

// Голямо число с подпис — за обобщения (по избор кликаемо)
export function StatTile({
  label,
  value,
  hint,
  color = COLORS.text,
  onPress,
}: {
  label: string;
  value: string;
  hint?: string;
  color?: string;
  onPress?: () => void;
}) {
  const Wrap: any = onPress ? Pressable : View;
  return (
    <Wrap
      onPress={onPress}
      style={{
        flex: 1,
        backgroundColor: COLORS.card,
        borderRadius: RADIUS.md,
        borderWidth: 1,
        borderColor: COLORS.border,
        paddingVertical: SP.md,
        paddingHorizontal: SP.md,
      }}
    >
      <Text style={{ fontSize: 11, color: COLORS.muted, fontWeight: '700', letterSpacing: 0.4, textTransform: 'uppercase' }}>
        {label}
      </Text>
      <Text style={{ fontSize: 22, fontWeight: '800', color, marginTop: 2 }} numberOfLines={1} adjustsFontSizeToFit>
        {value}
      </Text>
      {hint ? <Text style={{ fontSize: 12, color: COLORS.muted, marginTop: 1 }}>{hint}</Text> : null}
    </Wrap>
  );
}

// Единно празно състояние: икона + текст + по избор действие
export function EmptyState({
  icon,
  title,
  text,
  action,
}: {
  icon?: string;
  title: string;
  text?: string;
  action?: React.ReactNode;
}) {
  return (
    <View style={{ alignItems: 'center', paddingVertical: SP.xl, paddingHorizontal: SP.lg }}>
      {icon ? <Text style={{ fontSize: 40, marginBottom: SP.sm }}>{icon}</Text> : null}
      <Text style={{ fontSize: 16, fontWeight: '700', color: COLORS.text, textAlign: 'center' }}>{title}</Text>
      {text ? (
        <Text style={{ fontSize: 14, color: COLORS.muted, textAlign: 'center', marginTop: 4, lineHeight: 20 }}>{text}</Text>
      ) : null}
      {action ? <View style={{ marginTop: SP.md, alignSelf: 'stretch' }}>{action}</View> : null}
    </View>
  );
}

export function Row({ left, right }: { left: string; right: string }) {
  return (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', paddingVertical: 5, gap: 12 }}>
      <Text style={{ color: COLORS.muted, fontSize: 14 }}>{left}</Text>
      <Text style={{ color: COLORS.text, fontWeight: '600', flexShrink: 1, textAlign: 'right', fontSize: 14 }}>{right}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: COLORS.card,
    borderRadius: RADIUS.lg,
    padding: SP.lg,
    marginBottom: SP.md,
    borderWidth: 1,
    borderColor: COLORS.border,
    ...SHADOW,
  },
  title: { fontSize: 26, fontWeight: '800', color: COLORS.text, marginBottom: SP.md, letterSpacing: -0.4 },
  sectionLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: COLORS.muted,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    marginBottom: SP.sm,
  },
  label: { fontSize: 14, fontWeight: '700', color: COLORS.textDim, marginBottom: 5, marginTop: SP.sm },
  input: {
    backgroundColor: COLORS.card,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: RADIUS.sm,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    color: COLORS.text,
    minHeight: 48,
  },
  button: {
    borderRadius: RADIUS.sm,
    paddingVertical: 15,
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: SP.md,
    minHeight: 50,
  },
});
